import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import AdForgeLogo from '../../components/feature/AdForgeLogo';

type Status = 'loading' | 'needs_login' | 'accepting' | 'success' | 'error';

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<Status>('loading');
  const [teamName, setTeamName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Invalid invitation link — no token found.');
      return;
    }

    if (!user) {
      setStatus('needs_login');
      return;
    }

    acceptInvitation();
  }, [token, user]);

  const acceptInvitation = async () => {
    if (!token) return;
    setStatus('accepting');

    try {
      const { data, error } = await supabase.rpc('accept_team_invitation', {
        invitation_token: token,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; team_name?: string };

      if (!result.success) {
        throw new Error(result.error || 'Could not accept invitation');
      }

      setTeamName(result.team_name || 'your team');
      setStatus('success');

      setTimeout(() => {
        navigate('/dashboard/settings?tab=team');
      }, 3000);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to accept invitation');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-stone-100 max-w-md w-full p-8 text-center">
        <div className="flex justify-center mb-6">
          <AdForgeLogo size="sm" />
        </div>

        {status === 'loading' && (
          <>
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-stone-100 mx-auto mb-4">
              <i className="ri-loader-4-line text-stone-400 text-2xl animate-spin" />
            </div>
            <h2 className="text-lg font-bold text-stone-900 mb-2">Processing Invitation</h2>
            <p className="text-sm text-stone-500">Please wait...</p>
          </>
        )}

        {status === 'needs_login' && (
          <>
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-orange-100 mx-auto mb-4">
              <i className="ri-team-line text-orange-500 text-2xl" />
            </div>
            <h2 className="text-lg font-bold text-stone-900 mb-2">Team Invitation</h2>
            <p className="text-sm text-stone-500 mb-6">
              You've been invited to join a team on AdForge AI. Sign in or create an account to accept.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate(`/login?redirect=/accept-invite?token=${token}`)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-login-circle-line" />
                Sign In to Accept
              </button>
              <button
                onClick={() => navigate(`/signup?redirect=/accept-invite?token=${token}`)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold border border-stone-200 text-stone-700 hover:bg-stone-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line" />
                Create Account
              </button>
            </div>
          </>
        )}

        {status === 'accepting' && (
          <>
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-orange-100 mx-auto mb-4">
              <i className="ri-loader-4-line text-orange-500 text-2xl animate-spin" />
            </div>
            <h2 className="text-lg font-bold text-stone-900 mb-2">Accepting Invitation</h2>
            <p className="text-sm text-stone-500">Joining your team workspace...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-green-100 mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-green-500 text-2xl" />
            </div>
            <h2 className="text-lg font-bold text-stone-900 mb-2">Welcome to the team!</h2>
            <p className="text-sm text-stone-500 mb-2">
              You've successfully joined <span className="font-bold text-stone-800">{teamName}</span>.
            </p>
            <p className="text-xs text-stone-400">Redirecting you to your dashboard in a moment...</p>
            <div className="mt-5">
              <button
                onClick={() => navigate('/dashboard/settings?tab=team')}
                className="inline-flex items-center gap-2 text-sm font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-6 py-3 rounded-xl hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-dashboard-line" />
                Go to Dashboard
              </button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-red-100 mx-auto mb-4">
              <i className="ri-error-warning-line text-red-500 text-2xl" />
            </div>
            <h2 className="text-lg font-bold text-stone-900 mb-2">Invitation Error</h2>
            <p className="text-sm text-stone-500 mb-5">{errorMsg || 'This invitation link is invalid or has expired.'}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 text-sm font-semibold border border-stone-200 text-stone-700 px-6 py-3 rounded-xl hover:bg-stone-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line" />
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}